import { logger } from '../../../../utils/logger';

export function attachPeerConnectionDebugListeners(pc: RTCPeerConnection): void {
  pc.onicecandidate = (event: RTCPeerConnectionIceEvent) => {
    logger.debug(
      '[CXone DEBUG] ICE candidate:',
      event.candidate?.candidate || 'null (gathering complete)'
    );
  };
  pc.oniceconnectionstatechange = () => {
    logger.debug('[CXone DEBUG] ICE connection state changed:', pc.iceConnectionState);
  };
  pc.onicegatheringstatechange = () => {
    logger.debug('[CXone DEBUG] ICE gathering state changed:', pc.iceGatheringState);
  };
  pc.onconnectionstatechange = () => {
    logger.debug('[CXone DEBUG] Connection state changed:', pc.connectionState);
  };
  pc.onsignalingstatechange = () => {
    logger.debug('[CXone DEBUG] Signaling state changed:', pc.signalingState);
  };
  pc.ontrack = (event: RTCTrackEvent) => {
    logger.debug('[CXone DEBUG] ontrack event fired:', {
      kind: event.track.kind,
      id: event.track.id,
      enabled: event.track.enabled,
      muted: event.track.muted,
      readyState: event.track.readyState,
      streamCount: event.streams.length,
      streamIds: event.streams.map((s) => s.id),
    });

    event.track.onunmute = () => {
      logger.debug('[CXone DEBUG] Remote audio track UNMUTED - audio should now be flowing');
    };
    event.track.onmute = () => {
      logger.debug('[CXone DEBUG] Remote audio track MUTED - audio stopped flowing');
    };
    event.track.onended = () => {
      logger.debug('[CXone DEBUG] Remote audio track ENDED');
    };

    const audioEl = document.querySelector('audio');
    if (audioEl) {
      logger.debug('[CXone DEBUG] Audio element state after track:', {
        srcObject: audioEl.srcObject ? 'exists' : 'null',
        srcObjectId:
          audioEl.srcObject instanceof MediaStream ? (audioEl.srcObject as MediaStream).id : 'N/A',
        paused: audioEl.paused,
        muted: audioEl.muted,
        volume: audioEl.volume,
      });
    }
  };
}

export function logPeerConnectionState(
  pc: RTCPeerConnection | null | undefined,
  label: string
): void {
  if (pc) {
    logger.debug(`[CXone DEBUG] RTCPeerConnection state (${label}):`);
    logger.debug(`[CXone DEBUG]   connectionState: ${pc.connectionState}`);
    logger.debug(`[CXone DEBUG]   iceConnectionState: ${pc.iceConnectionState}`);
    logger.debug(`[CXone DEBUG]   iceGatheringState: ${pc.iceGatheringState}`);
    logger.debug(`[CXone DEBUG]   signalingState: ${pc.signalingState}`);
  } else {
    logger.debug(`[CXone DEBUG] RTCPeerConnection (${label}): NOT YET CREATED`);
  }
}

export function watchPeerConnectionDebug(jsSession: {
  connection?: RTCPeerConnection | null;
  on: (event: string, handler: (...args: unknown[]) => void) => void;
}): void {
  logPeerConnectionState(jsSession.connection, 'immediate');
  setTimeout(() => logPeerConnectionState(jsSession.connection, 'after 50ms'), 50);
  setTimeout(() => logPeerConnectionState(jsSession.connection, 'after 200ms'), 200);
  setTimeout(() => logPeerConnectionState(jsSession.connection, 'after 500ms'), 500);
  setTimeout(() => logPeerConnectionState(jsSession.connection, 'after 1000ms'), 1000);

  const pc = jsSession.connection;
  if (pc) {
    attachPeerConnectionDebugListeners(pc);
    return;
  }

  logger.debug(
    '[CXone DEBUG] RTCPeerConnection not available immediately, waiting for peerconnection event...'
  );
  jsSession.on('peerconnection', (...args: unknown[]) => {
    const data = args[0] as { peerconnection: RTCPeerConnection };
    logger.debug('[CXone DEBUG] peerconnection event fired, setting up PC listeners');
    attachPeerConnectionDebugListeners(data.peerconnection);
  });
}
